import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { isValidEmail } from "@/lib/validators";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) return toast.error("Please enter a valid email address.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success(data.message);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8"><Logo /></div>
        {sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <MailCheck className="mx-auto text-emerald mb-4" size={40} />
            <h1 className="font-display font-bold text-2xl mb-2">Check your inbox</h1>
            <p className="text-sm text-muted">If an account exists for <span className="text-ink">{email}</span>, we've sent a link to reset your password. It expires in 1 hour.</p>
            <Link to="/login" className="text-azure text-sm mt-6 inline-block hover:underline">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form">
            <h1 className="font-display font-bold text-2xl mb-1">Reset your password</h1>
            <p className="text-sm text-muted mb-6">Enter your email and we'll send you a secure reset link.</p>
            <label className="label-mono text-faint">Email</label>
            <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 mb-5 w-full rounded-lg border border-white/12 bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-azure focus:outline-none focus:ring-2 focus:ring-azure/40 transition-colors duration-200" />
            <button disabled={busy} data-testid="forgot-submit"
              className="w-full rounded-full bg-azure py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <p className="mt-6 text-sm text-muted text-center">
              Remembered it? <Link to="/login" className="text-azure hover:underline">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
